import React, { useState } from "react";
import { useAuth, hasPermission } from "../../context/AuthContext";
import { useDepartmentRuntime } from "../../hooks/useDepartmentRuntime";
import { PageHeader, SectionCard } from "../../components/Ui";
import { Button } from "../../components/ui/button";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import {
  RuntimeActorLine,
  RuntimeBreakdownBanner,
  RuntimeLogHistory,
  RuntimeSectionHeader,
  RuntimeUptimeBlock,
} from "../../components/production/RuntimeShared";
import { PauseCircle, PlayCircle, Wrench } from "lucide-react";
import { formatElapsedVerbose, elapsedParts } from "../../components/RuntimeCounter";

export default function DepartmentRuntimePage({
  department,
  eyebrow,
  title,
  description,
  testId,
  sectionEyebrow,
}) {
  const { user } = useAuth();
  const canControl = hasPermission(user, "production.monitoring.control");
  const { sections, recentLogs, summary, loading, submitting, startSection, stopSection } = useDepartmentRuntime(department);

  const [startTarget, setStartTarget] = useState(null);
  const [stopTarget, setStopTarget] = useState(null);
  const [stopMode, setStopMode] = useState("normal");
  const [breakdownReason, setBreakdownReason] = useState("");
  const [stopRemarks, setStopRemarks] = useState("");

  const resetStopDialog = () => {
    setStopTarget(null);
    setStopMode("normal");
    setBreakdownReason("");
    setStopRemarks("");
  };

  const breakdownLogCount = recentLogs.filter((l) => l.status === "BREAKDOWN").length;

  return (
    <div className="space-y-6 sm:space-y-8" data-testid={testId}>
      <PageHeader eyebrow={eyebrow} title={title} description={description} />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <SummaryTile label="Sections" value={summary.total_sections ?? "—"} />
        <SummaryTile label="Running" value={summary.running_count ?? 0} accent />
        <SummaryTile label="Stopped" value={summary.stopped_count ?? 0} />
        <SummaryTile label="Breakdown" value={breakdownLogCount} warn={breakdownLogCount > 0} />
      </div>

      {loading && !sections.length ? (
        <p className="text-sm text-muted-foreground py-12 text-center">Loading status…</p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {sections.map((section) => (
            <SectionRuntimeCard
              key={section.slug}
              section={section}
              sectionEyebrow={sectionEyebrow}
              canControl={canControl}
              onStart={() => setStartTarget(section)}
              onStop={() => {
                setStopTarget(section);
                setStopMode("normal");
              }}
            />
          ))}
        </div>
      )}

      <SectionCard title="Runtime history">
        <RuntimeLogHistory logs={recentLogs} />
      </SectionCard>

      <Dialog open={!!startTarget} onOpenChange={(open) => !open && setStartTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Start {startTarget?.name}</DialogTitle>
            <DialogDescription>
              Recorded as started by <strong>{user?.name}</strong> ({user?.role}).
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setStartTarget(null)} disabled={submitting}>Cancel</Button>
            <Button
              className="bg-bansal-sage hover:bg-bansal-sageDark text-white"
              disabled={submitting}
              onClick={async () => {
                const ok = await startSection(startTarget.slug);
                if (ok) setStartTarget(null);
              }}
            >
              Confirm start
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!stopTarget} onOpenChange={(open) => !open && resetStopDialog()}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Stop {stopTarget?.name}</DialogTitle>
            <DialogDescription>
              Recorded as stopped by <strong>{user?.name}</strong> ({user?.role}).
              {stopTarget?.status === "RUNNING" && stopTarget?.start_time && (
                <span className="block mt-2 font-mono text-primary">
                  Uptime: {formatElapsedVerbose(elapsedParts(stopTarget.start_time))}
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-2">
              <Button type="button" variant={stopMode === "normal" ? "default" : "outline"} onClick={() => setStopMode("normal")}>
                Normal stop
              </Button>
              <Button type="button" variant={stopMode === "breakdown" ? "destructive" : "outline"} onClick={() => setStopMode("breakdown")}>
                <Wrench className="h-4 w-4 mr-2" />Breakdown
              </Button>
            </div>
            {stopMode === "breakdown" ? (
              <div className="space-y-2">
                <Label>Breakdown reason (required)</Label>
                <Textarea value={breakdownReason} onChange={(e) => setBreakdownReason(e.target.value)} rows={3} />
              </div>
            ) : (
              <div className="space-y-2">
                <Label>Remarks (optional)</Label>
                <Textarea value={stopRemarks} onChange={(e) => setStopRemarks(e.target.value)} rows={2} />
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={resetStopDialog} disabled={submitting}>Cancel</Button>
            <Button
              variant={stopMode === "breakdown" ? "destructive" : "default"}
              className={stopMode === "normal" ? "bg-bansal-sage hover:bg-bansal-sageDark text-white" : ""}
              disabled={submitting || (stopMode === "breakdown" && !breakdownReason.trim())}
              onClick={async () => {
                const ok = await stopSection(stopTarget.slug, {
                  stopType: stopMode,
                  breakdownReason,
                  remarks: stopRemarks,
                });
                if (ok) resetStopDialog();
              }}
            >
              Confirm
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function SectionRuntimeCard({ section, sectionEyebrow, canControl, onStart, onStop }) {
  const running = section.status === "RUNNING";
  const breakdown = section.status === "BREAKDOWN";
  const borderClass = running
    ? "border-bansal-sage/50 bg-bansal-sage/5"
    : breakdown
      ? "border-red-300 bg-red-50/40"
      : "border-border bg-card";

  return (
    <div className={`rounded-xl border-2 ${borderClass} p-4 sm:p-5 flex flex-col gap-4 min-h-[240px]`} data-testid={`runtime-section-${section.slug}`}>
      <RuntimeSectionHeader eyebrow={sectionEyebrow} title={section.name} status={section.status} />
      <RuntimeUptimeBlock section={section} testIdPrefix={section.slug} />
      <RuntimeBreakdownBanner reason={section.breakdown_reason} />
      <RuntimeActorLine label="Started by" name={section.started_by_name} role={section.started_by_role} />
      {canControl && (
        <div className="grid grid-cols-2 gap-2 mt-auto pt-2">
          <Button className="min-h-[48px] bg-bansal-sage hover:bg-bansal-sageDark text-white" disabled={running || breakdown} onClick={onStart}>
            <PlayCircle className="h-4 w-4 mr-2" />START
          </Button>
          <Button className="min-h-[48px] border-red-300 text-red-800 hover:bg-red-50" variant="outline" disabled={!running} onClick={onStop}>
            <PauseCircle className="h-4 w-4 mr-2" />STOP
          </Button>
        </div>
      )}
    </div>
  );
}

function SummaryTile({ label, value, accent, warn }) {
  const cls = warn ? "border-red-300 bg-red-50/50" : accent ? "border-bansal-sage/40 bg-bansal-sage/5" : "border-border bg-card";
  return (
    <div className={`rounded-xl border px-4 py-3 ${cls}`}>
      <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{label}</div>
      <div className={`font-serif text-2xl mt-1 tabular-nums ${accent ? "text-primary" : warn ? "text-red-800" : "text-foreground"}`}>{value}</div>
    </div>
  );
}
